<script setup>
/**
 * @description 全局 SvgIcon 组件，通过 import.meta.glob 实现按需加载 assets/icons 下的 svg 文件
 * @param {string} name - 图标名称 (文件名)
 * @param {string} dir - 目录名称 (默认 mono)
 * @param {string} color - 图标颜色
 * @param {string | number} size - 图标大小 (默认单位 px)
 * @param {boolean} filled - 是否保留原始颜色 (如果是 multi 目录会自动设为 true)
 * @example
 * <SvgIcon name="user" dir="mono" color="red" size="20" />
 */
import { ref, computed, watch, onMounted } from 'vue'

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  dir: {
    type: String,
    default: 'mono',
  },
  color: {
    type: String,
    default: '',
  },
  size: {
    type: [String, Number],
    default: 16,
  },
  filled: {
    type: Boolean,
    default: false,
  },
})

// 判定是否保持原始颜色
const isMulti = computed(() => props.dir === 'multi' || props.filled)

// 使用 import.meta.glob 懒加载所有 SVG，使用 ?raw 获取原始字符串内容
// 注意：路径必须是相对于当前组件文件的相对路径
const icons = import.meta.glob('../../assets/icons/**/*.svg', {
  query: '?raw',
  import: 'default',
})

const svgContent = ref('')

const COLOR_ATTR_REGEXP =
  /\s+(fill|stroke)=["'](?!none\b|currentColor\b|transparent\b|url\()[^"']+["']/gi
const STYLE_COLOR_REGEXP =
  /(^|;)\s*(fill|stroke)\s*:\s*(?!none\b|currentColor\b|transparent\b|url\()[^;]+/gi

const normalizeMonoSvgColor = (svg) => {
  return svg
    .replace(COLOR_ATTR_REGEXP, (_, attr) => ` ${attr}="currentColor"`)
    .replace(/\s+style=(["'])(.*?)\1/gi, (_, quote, style) => {
      const normalizedStyle = style.replace(
        STYLE_COLOR_REGEXP,
        (match, prefix, prop) => `${prefix}${prop}: currentColor`
      )
      return ` style=${quote}${normalizedStyle}${quote}`
    })
}

/**
 * 预处理 SVG 字符串，确保响应式尺寸并清理冗余
 */
const processSvg = (raw, preserveOriginalColor = false) => {
  if (!raw) return ''

  // 1. 移除 xml 声明、doctype 和注释，保证注入 HTML 时的干净
  let svg = raw
    .replace(/<\?xml.*?\?>/i, '')
    .replace(/<!DOCTYPE.*?>/i, '')
    .replace(/<!--.*?-->/gs, '')

  // 2. 移除原有硬编码的尺寸属性（如果存在）
  // 如果没有 viewBox 但有 width/height，则补充 viewBox 确保缩放正常
  svg = svg.replace(/<svg([^>]*)>/i, (match, attrs) => {
    let newAttrs = attrs.replace(/\s+(width|height)=["'][^"']*["']/gi, '')

    // 检查是否有 viewBox
    if (!/viewBox=["'][^"']*["']/i.test(attrs)) {
      const wMatch = attrs.match(/\bwidth=["']([^"']*)["']/i)
      const hMatch = attrs.match(/\bheight=["']([^"']*)["']/i)
      if (wMatch && hMatch) {
        newAttrs += ` viewBox="0 0 ${wMatch[1]} ${hMatch[1]}"`
      }
    }

    return `<svg${newAttrs} width="100%" height="100%">`
  })

  if (!preserveOriginalColor) {
    svg = normalizeMonoSvgColor(svg)
  }

  return svg
}

/**
 * 动态加载对应的 SVG 文件
 */
const loadIcon = async () => {
  if (!props.name) {
    svgContent.value = ''
    return
  }

  // 构造匹配键名。glob 键名包含 ../../assets/icons/
  const targetPathPart = `assets/icons/${props.dir}/${props.name}.svg`

  // 在 glob 结果中寻找匹配的 loader
  const iconEntries = Object.entries(icons)
  const targetEntry = iconEntries.find(([key]) => key.endsWith(targetPathPart))

  if (targetEntry && typeof targetEntry[1] === 'function') {
    try {
      const raw = await targetEntry[1]()
      svgContent.value = processSvg(raw, isMulti.value)
    } catch (err) {
      console.error(`[SvgIcon] Failed to load icon: ${targetPathPart}`, err)
      svgContent.value = ''
    }
  } else {
    // 找不到图标时的回退逻辑
    svgContent.value = ''
    if (import.meta.dev) {
      console.warn(`[SvgIcon] Icon not found at ${targetPathPart}`)
    }
  }
}

const iconSize = computed(() => {
  const s = props.size
  if (!s) return undefined
  if (typeof s === 'number') return `${s}px`
  // 如果是纯数字字符串，追加 px
  if (/^\d+$/.test(s)) return `${s}px`
  return s
})

// 监听 props 变化重新加载
watch(() => [props.name, props.dir, props.filled], loadIcon)

// 首次挂载加载
onMounted(() => {
  loadIcon()
})
</script>

<template>
  <i
    class="svg-icon-container"
    :class="[{ 'svg-icon--filled': isMulti }]"
    :style="{
      width: iconSize,
      height: iconSize,
      color: color,
    }"
    v-html="svgContent"
  />
</template>

<style scoped>
.svg-icon-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: -0.125em;
  line-height: 0;
  flex-shrink: 0;
  /* 默认继承父级颜色，使用继承方式以便 SVG 内部属性 (如 fill="none") 可以覆盖它 */
  fill: currentColor;
}

.svg-icon-container :deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
  outline: none;
}

.svg-icon--filled {
  /* filled 模式不强制继承 fill，由 SVG 内部属性控制 */
  fill: none;
}
</style>
